/**
 * Checkout helpers that are still stubbed (AR-290): USPS address verification
 * and the forgot-password email. Account creation, coupons and payment are
 * real since AR-420 — see `api.ts`.
 *
 *  USPS address verification
 *    - ZIP `00000`                  → USPS outage ("use as entered")
 *    - street with a correctable spelling/suffix → suggestion returned
 *    - otherwise unrecognised street → no match
 */

import type { AddressDraft } from './types';

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const requestPasswordReset = async (email: string): Promise<{ status: 'sent' }> => {
  await delay(400);
  void email;
  return { status: 'sent' };
};

export type AddressVerification =
  | { status: 'verified'; normalized: AddressDraft }
  | { status: 'suggestion'; suggested: AddressDraft }
  | { status: 'no_match' }
  | { status: 'unavailable' };

/** Common street-suffix and spelling fixes the USPS API would normalise for us. */
const STREET_CORRECTIONS: Array<[RegExp, string]> = [
  [/\bstreet\b/gi, 'St'],
  [/\bavenue\b/gi, 'Ave'],
  [/\broad\b/gi, 'Rd'],
  [/\bdrive\b/gi, 'Dr'],
  [/\blane\b/gi, 'Ln'],
  [/\bboulevard\b/gi, 'Blvd'],
  [/\bcourt\b/gi, 'Ct'],
  [/\bplace\b/gi, 'Pl'],
  [/\bterrace\b/gi, 'Ter'],
  [/\bcircle\b/gi, 'Cir'],
  [/\bparkway\b/gi, 'Pkwy'],
  [/\bhighway\b/gi, 'Hwy'],
  [/\bharbour\b/gi, 'Harbor'],
  [/\bvew\b/gi, 'View'],
  [/\bcentre\b/gi, 'Center'],
];

const STANDARD_SUFFIX =
  /\b(st|ave|rd|dr|ln|blvd|ct|pl|ter|cir|pkwy|hwy|way|trl|loop|run|row|walk|sq|aly|byp)\.?$/i;

export const verifyAddress = async (address: AddressDraft): Promise<AddressVerification> => {
  await delay(600);

  if (address.zip.trim() === '00000') return { status: 'unavailable' };

  const corrected = STREET_CORRECTIONS.reduce(
    (street, [pattern, replacement]) => street.replace(pattern, replacement),
    address.street.trim(),
  );

  if (corrected.toLowerCase() !== address.street.trim().toLowerCase()) {
    return {
      status: 'suggestion',
      suggested: { ...address, street: corrected, zip: `${address.zip.slice(0, 5)}-2288` },
    };
  }

  if (!STANDARD_SUFFIX.test(corrected)) return { status: 'no_match' };

  return { status: 'verified', normalized: { ...address, street: corrected } };
};
